import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Container,
  Dimmer,
  Grid,
  Item,
  Label,
  Loader,
  Form,
  Icon,
  Modal,
  Segment,
  Table,
} from 'semantic-ui-react';
import { fetchM } from '../../../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../../../actions/flash';
import { namespaces, TRANSLATION_FILES } from '../../../constants/translation';
import { decodeSearch } from '../../../utils/url';
// components
import LoanAmount from '../../../components/LoanAmount';
// utils
import { minorToMajor } from '../../../utils/numbers/currencyMinor';

class UnitReservationRefunds extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        settings: PropTypes.shape({
          notesExternalPlaceholder: PropTypes.string,
        }),
      }),
    }),
    location: PropTypes.shape({
      query: PropTypes.object,
      search: PropTypes.string,
    }),
    auth: PropTypes.shape({
      isHeadquarters: PropTypes.bool,
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    loading: PropTypes.bool,
    refundToken: PropTypes.string,
    fetchRefund: PropTypes.func,
    fetchHeaderCounts: PropTypes.func,
    onUpdateOpportunity: PropTypes.func,
    pendingCancelUnitsTab: PropTypes.bool,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
    appConfig: state.appConfig,
  })
  static getPropsToState = (props) => {
    return {
      data: props.data || [],
      loading: props.loading || false,
    };
  }
  constructor(props) {
    super(props);
    this.state = {
      ...UnitReservationRefunds.getPropsToState(props),
      isConfirmOpen: false,
      confirmType: null,
      selectedRefund: null,
      isSubmitting: false,
    };
    this._isMounted = false;
  }
  componentDidMount() {
    this._isMounted = true;
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    if (nextProps.data !== this.props.data || nextProps.loading !== this.props.loading) {
      this.setState(UnitReservationRefunds.getPropsToState(nextProps));
    }
  }

  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, res);
    });
  }
  handleOpenConfirm = (e, type, refund) => {
    e.stopPropagation();
    this.setState({
      isConfirmOpen: true,
      confirmType: type,
      selectedRefund: refund,
    });
  }

  handleCloseConfirm = () => {
    this.setState({ isConfirmOpen: false, confirmType: null });
  }

  handleConfirmAction = async () => {
    const { refundToken, t, fetchRefund, fetchHeaderCounts, onUpdateOpportunity } = this.props;
    const { confirmType, selectedRefund } = this.state;
    const { store } = this.context;

    if (!selectedRefund) return;

    const url = `/unit_reservation_refunds/${selectedRefund.id}/status/`;
    this.setState({ isSubmitting: true });

    try {
      const action = fetchM(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': refundToken,
        },
        body: JSON.stringify({
          status: confirmType,
          rejectionReason: '-',
          amountMinorRefunded: confirmType === 'approved' ? (selectedRefund ? selectedRefund.amountMinor : 0) : 0,
        }),
      });

      const response = await store.dispatch(action);

      if (response && response.ok) {
        // Update local state immediately to show the new status
        this.setState(prevState => ({
          data: prevState.data.map(refund =>
            refund.id === selectedRefund.id
              ? { ...refund, status: confirmType }
              : refund,
          ),
        }));

        store.dispatch(flashSuccessMessage({
          header: t('Success', { ns: TRANSLATION_FILES.constants }),
          message: t('Refund status updated successfully', { ns: TRANSLATION_FILES.translation }),
        }));

        // Update opportunity status based on refund decision
        if (onUpdateOpportunity) {
          const opportunityStatus = confirmType === 'approved' ? 'refunded' : 'refund_declined';
          onUpdateOpportunity(opportunityStatus, '', selectedRefund.opportunityID);
        }

        const query = decodeSearch(this.props.location.search);
        if (fetchRefund) {
          fetchRefund(query);
        }
        if (fetchHeaderCounts) {
          fetchHeaderCounts(query.search || '');
        }

        this.handleCloseConfirm();
      }
    } catch (err) {
      store.dispatch(flashErrorMessage({
        header: t('Error', { ns: TRANSLATION_FILES.constants }),
        message: t('Error updating refund status', { ns: TRANSLATION_FILES.translation }),
      }));
    } finally {
      if (this._isMounted) {
        this.setState({ isSubmitting: false });
      }
    }
  };
  render() {
    const { t, loading } = this.props;
    const { isConfirmOpen, confirmType, isSubmitting, selectedRefund } = this.state;
    const isApprove = confirmType === 'approved';
    return (
      <Container>
        <Grid verticalAlign="middle">
          {/* List Data */}
          <Grid.Row className="app--listdata app--mblr">
            <Grid.Column className="app--mblc">
              <br className="large-hide" />
              <Dimmer style={{ zIndex: 20 }} active={this.props.loading} inverted>
                <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
              </Dimmer>
              {
                this.state.data.length > 0 ?
                  <Grid columns={2} className="equal height">
                    <Grid.Row className="manage-booking-list-row">
                      <Grid.Column className="manage-booking-list-col" width={16}>
                        <Table celled className="manage-bookings">
                          <Table.Header className="mobile-hide">
                              <Table.Row>
                                <Table.HeaderCell width={4}>{t('Booking', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
                                <Table.HeaderCell width={3}>{t('Amount', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
                                <Table.HeaderCell width={3}>{t('Reason', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
                                <Table.HeaderCell width={3}>{t('Bank Info', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
                                <Table.HeaderCell textAlign="center" collapsing>{t('Action', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                          <Table.Body>
                            {this.state.data.map((refund, idx) => (
                              <Table.Row key={refund.id || idx}>
                                {/* COLUMN 1: Booking (Project & Purchaser) */}
                                <Table.Cell>
                                  <div style={{ lineHeight: '1.2' }}>
                                    <a href={`/opportunities/${refund.opportunityID}/`} style={{ color: '#4183c4', fontWeight: 'bold' }}>
                                      {refund.projectName}
                                    </a>
                                    <br />
                                    <span>{refund.purchaserName}</span>
                                  </div>
                                </Table.Cell>

                                {/* COLUMN 2: Amount (Formatted from minor units) */}
                                <Table.Cell textAlign="left">
                                  <LoanAmount
                                    format="0,000.00"
                                    amount={minorToMajor(refund.amountMinor, refund.currencyCode)}
                                  />
                                </Table.Cell>

                                {/* COLUMN 3: Reason */}
                                <Table.Cell>
                                  {refund.reason || '-'}
                                </Table.Cell>

                                {/* COLUMN 4: Bank Info (With Line Breaks) */}
                                <Table.Cell>
                                  <div style={{ lineHeight: '1.3' }}>
                                    <b>{refund.bankAccountName}</b>
                                    <br />
                                    <span style={{ fontFamily: 'monospace' }}>{refund.bankAccountNumber}</span>
                                    <br />
                                    <small style={{ color: 'grey' }}>{refund.bankName}</small>
                                  </div>
                                </Table.Cell>

                                {/* COLUMN 5: Action */}
                                <Table.Cell collapsing textAlign="right">
                                  {refund.status === 'pending' ? (
                                    <React.Fragment>
                                      {/* Desktop View Buttons (Hidden on mobile via Semantic UI or custom CSS) */}
                                      <Button
                                        className="mobile-hide"
                                        floated="right"
                                        compact
                                        size="mini"
                                        negative
                                        onClick={(e) => this.handleOpenConfirm(e, 'declined', refund)}
                                      >
                                        {t('Decline', { ns: TRANSLATION_FILES.screens })}
                                      </Button>
                                      <Button
                                        className="mobile-hide"
                                        floated="right"
                                        compact
                                        size="mini"
                                        color="green"
                                        onClick={(e) => this.handleOpenConfirm(e, 'approved', refund)}
                                      >
                                        {t('Complete', { ns: TRANSLATION_FILES.screens })}
                                      </Button>

                                      {/* Mobile View Buttons (Floating/Compact styles) */}
                                      <Button
                                        className="large-hide small-hide tablet-hide"
                                        floated="right"
                                        compact
                                        size="mini"
                                        negative
                                        onClick={(e) => this.handleOpenConfirm(e, 'declined', refund)}
                                      >
                                        {t('Decline', { ns: TRANSLATION_FILES.screens })}
                                      </Button>
                                      <Button
                                        className="large-hide small-hide tablet-hide"
                                        floated="right"
                                        compact
                                        size="mini"
                                        color="green"
                                        onClick={(e) => this.handleOpenConfirm(e, 'approved', refund)}
                                      >
                                        {t('Complete', { ns: TRANSLATION_FILES.screens })}
                                      </Button>
                                    </React.Fragment>
                                        ) : (
                                          /* Show status label if already processed */
                                          <div style={{ textAlign: 'center' }}>
                                            <Label color={refund.status === 'approved' ? 'green' : 'red'} basic size="mini">
                                              {refund.status === 'approved' ? t('Completed', { ns: TRANSLATION_FILES.screens }) : t('Declined', { ns: TRANSLATION_FILES.screens })}
                                            </Label>
                                          </div>
                                        )}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  : (
                    <Grid>
                      <Grid.Row>
                        <Grid.Column>
                          <Item style={{}}>
                            <Item.Content>
                              {
                              !this.props.loading ?
                                <Segment textAlign="center">
                                  <p>{t('No pending bookings found.', { ns: TRANSLATION_FILES.translation })}</p>
                                </Segment>
                              : ''
                              }
                            </Item.Content>
                          </Item>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  )
                }
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Modal
          open={isConfirmOpen}
          onClose={this.handleCloseConfirm}
          size={isApprove ? 'mini' : 'tiny'}
          closeIcon
        >
          <Modal.Header>{isApprove ? t('Approve refund request', { ns: TRANSLATION_FILES.translation }) : t('Reject refund request', { ns: TRANSLATION_FILES.translation })}</Modal.Header>
          <Modal.Content>
            {isApprove ? (
              <div>
                <p className="confirm-approve-text">
                  {t('Are you sure you want to approve this refund?', { ns: TRANSLATION_FILES.translation })}
                </p>
                <p className="approve-info-text" style={{ color: '#555555', fontSize: '0.9rem' }}>
                  {t('Once approved, refunds will be processed for all received payments.', { ns: TRANSLATION_FILES.translation })}
                </p>
              </div>
            ) : (
              <div className="reject-refund-wrapper">
                <p className="confirm-reject-text">{t('Are you sure you want to reject this refund request?', { ns: TRANSLATION_FILES.screens })}</p>
                <div style={{ marginTop: '1rem' }}>
                  <Icon name="info circle" className="info-icon" style={{ color: '#0288D1', marginRight: '6px' }} />
                  <span className="reject-info-text" style={{ color: '#555555', fontSize: '0.9rem' }}>
                    {t('A notification will be sent to the agent and buyer upon confirmation.', { ns: TRANSLATION_FILES.translation })}
                  </span>
                </div>
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={this.handleCloseConfirm}>{t('Cancel', { ns: TRANSLATION_FILES.translation })}</Button>
            <Button color="teal" onClick={this.handleConfirmAction} loading={isSubmitting} disabled={loading || isSubmitting}>
              {t('Confirm', { ns: TRANSLATION_FILES.translation })}
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(withRouter(UnitReservationRefunds));



// WEBPACK FOOTER //
// ./src/containers/Opportunities/ManageRefunds/UnitReservationRefunds.jsx